import './DatasetsList.scss';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { compact } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { useHistory } from 'react-router';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { EditIcon } from '../../../../design/static/icons/EditIcon';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { RowLayout } from '../../../../modules/common/layouts/rowLayout/RowLayout';
import { DatasetStatusBadge } from '../../../../modules/common/statusesBadges/datasetStatus/DatasetStatusBadge';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { EnableFor } from '../../../containers/EnableForRole';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { IPagingInfoWithOrder } from '../../../models/paginationInfo.model';
import { Home } from '../../../routes/config/Home';
import { useDatasetPermissions } from '../datasets.permissions';
import { IDatasetListItemViewModel } from '../datasets.store';

interface IProps {
  datasetsList: IDatasetListItemViewModel[];
  isLoading: boolean;
  workspaceId: string;
  datasetsPaging: IPagingInfoWithOrder;
  onShowDatasetDeleteConfirmationModal: (datasetId: string) => void;
  onOrderingChange: (orderBy: string, orderType: string) => Promise<void>;
  onPaginationChange: (pageNumber: number, pageSize: number) => Promise<void>;
}

export const DatasetsList = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const history = useHistory();
  const permissions = useDatasetPermissions();

  const columns = (): IGColumnProps<IDatasetListItemViewModel>[] => {
    return compact([
      {
        sortBy: 'name',
        renderer: renderName,
        headerName: t('name'),
      },
      {
        sortBy: 'status',
        headerName: t('status'),
        renderer: (data) => <DatasetStatusBadge datasetStatus={data.status} />,
      },
      permissions.canSeeDatasetCreatedBy().allowed && {
        sortBy: 'createdBy',
        renderer: renderCreatedBy,
        headerName: t('created_by'),
      },
      {
        sortBy: 'projectsCount',
        headerName: t('number_of_projects'),
        renderer: (x) => x.projectsCount,
      },
      {
        sortBy: 'imagesCount',
        headerName: t('number_of_images_short'),
        renderer: (x) => x.imagesCount,
      },
      {
        sortBy: 'imagesSize',
        headerName: t('size_of_images'),
        renderer: renderSize,
      },
      {
        sortBy: 'createdDate',
        headerName: t('created'),
        renderer: renderCreatedDate,
      },
      {
        sortBy: 'lastModified',
        headerName: t('last_modified_date'),
        renderer: renderModifiedDate,
      },
      {
        headerName: t('actions'),
        width: 'min-content',
        renderer: renderActions,
        actions: true,
      },
    ]);
  };

  const renderSize = (item: IDatasetListItemViewModel): ReactElement => {
    return (
      <RowLayout size='xs'>
        {item.imagesSize.toFixed(2)}
        {item.imagesSize >= 1024 && (
          <FontAwesomeIcon
            title={t('dataset_size_limit_tooltip')}
            className='warning-icon'
            icon={faTriangleExclamation}
          />
        )}
      </RowLayout>
    );
  };

  const renderActions = (item: IDatasetListItemViewModel): ReactElement => {
    const disabled = item.projectsCount > 0;
    return (
      <>
        <EnableFor hasPermission={permissions.canViewDatasets()}>
          <IconButton
            title={t('edit_dataset')}
            color='primary'
            size='sm'
            onClick={() => {
              history.push(Home.Datasets.Details.Images.withParams({ datasetId: item.id, workspaceId: props.workspaceId, page: 1 }));
            }}
          >
            <EditIcon />
          </IconButton>
        </EnableFor>
        <EnableFor hasPermission={permissions.canDeleteDataset(item.status, item.createdById)}>
          <IconButton
            size='sm'
            onClick={(): void => {
              props.onShowDatasetDeleteConfirmationModal(item.id);
            }}
            disabled={disabled}
            title={disabled ? t('delete_error_dataset_assigned_to_project') : t('delete_dataset_tooltip')}
          >
            <TrashIcon />
          </IconButton>
        </EnableFor>
      </>
    );
  };

  const renderName = (item: IDatasetListItemViewModel): ReactElement => {
    const title = `${item.name}\n${item.description}`;

    return (
      <div
        title={title}
        className='persist-reduntant-spaces'
      >
        <S_Link2
          color='text'
          to={Home.Datasets.Details.Images.withParams({ datasetId: item.id, workspaceId: props.workspaceId, page: 1 })}
        >
          {item.name}
        </S_Link2>
      </div>
    );
  };

  const renderCreatedBy = (item: IDatasetListItemViewModel): ReactElement => {
    const createdBy = item.createdBy;
    return <span title={createdBy}>{createdBy}</span>;
  };

  const renderCreatedDate = (item: IDatasetListItemViewModel): ReactElement => <>{toLocaleDateTimeString(item.createdDate)}</>;

  const renderModifiedDate = (item: IDatasetListItemViewModel): ReactElement => <>{toLocaleDateTimeString(item.lastModified)}</>;

  return (
    <GTable<IDatasetListItemViewModel>
      className='datasets-list'
      isLoading={props.isLoading}
      columns={columns()}
      noItemsPlaceholder={t('no_datasets')}
      items={props.datasetsList}
      paginationProps={{
        pageNumber: props.datasetsPaging.pageNumber,
        pageSize: props.datasetsPaging.pageSize,
        totalCount: props.datasetsPaging.totalCount,
        onChange: props.onPaginationChange,
      }}
      onSortChanged={props.onOrderingChange}
      sortingModel={[
        {
          orderBy: props.datasetsPaging.orderBy || '',
          orderType: props.datasetsPaging.orderType || '',
        },
      ]}
    />
  );
});
